import { useState, useTransition } from 'react';
import { RouterLinkAs } from '@/lib/router-link';
import {
  AlertDialog,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  I,
  Row,
} from '@leanin/ui';

// Split-button action menu used on every row of the broadcasts list.
// The primary half links to the edit page when the row is still editable
// (draft / future-scheduled — both mutable per Frozen Decision 10) or to
// the read-only detail page once the row is "locked" (sent, or a
// scheduled row whose send time has elapsed and the dispatcher is now
// mid-flight). The chevron half opens a Radix menu with row-status-aware
// items:
//   • editable → Duplicate, Delete (or "Cancel send" for future-scheduled)
//   • locked   → Duplicate (sent + sending rows are immutable; no
//                destructive path — the dispatcher's already running and
//                recipient_events is a regulatory record once dispatched)
//
// `primaryHref` is provided by the parent so the same component serves
// platform-admin (/admin/broadcasts/...) and group-admin
// (/networks/:id/admin/broadcasts/..., /circles/:id/admin/..., etc.)
// scopes. `locked` is also computed by the parent because only it has
// access to `scheduledSendAt` for the past-due check.

type Action = {
  readonly key: 'duplicate' | 'delete';
  readonly label: string;
  readonly destructive?: boolean;
  readonly run: (id: string) => Promise<void>;
};

type Props = {
  readonly broadcastId: string;
  readonly subject: string;
  readonly status: string;
  readonly primaryHref: string;
  /** True when the row can no longer be edited — either sent, or a
   *  scheduled row whose send time has elapsed and the dispatcher has
   *  started processing it. */
  readonly locked: boolean;
  readonly onDuplicate: (id: string) => Promise<void>;
  readonly onDelete: (id: string) => Promise<void>;
};

export function BroadcastActionMenu({
  broadcastId,
  subject,
  status,
  primaryHref,
  locked,
  onDuplicate,
  onDelete,
}: Props) {
  const [pending, start] = useTransition();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isScheduled = status === 'scheduled';
  const primaryLabel = locked ? 'Details' : 'Continue editing';

  // Locked rows (sent + sending) are immutable. Drafts and
  // future-scheduled rows both hit the same DELETE endpoint; the menu
  // label is the only thing that differs ("Cancel send" reads better
  // than "Delete" for scheduled).
  const destructiveItem: Action | null = locked
    ? null
    : {
        key: 'delete',
        label: isScheduled ? 'Cancel send' : 'Delete',
        destructive: true,
        run: async () => {
          setDeleteOpen(true);
        },
      };

  // "Edit" used to live as a dropdown item for drafts, but the primary
  // "Continue editing" button already covers that path — the dropdown
  // entry was redundant.
  const items: ReadonlyArray<Action> = [
    { key: 'duplicate', label: 'Duplicate', run: onDuplicate },
    ...(destructiveItem ? [destructiveItem] : []),
  ];

  const dialogTitle = isScheduled
    ? `Cancel the scheduled send of '${subject}'?`
    : `Delete '${subject}'?`;
  const dialogBody = isScheduled
    ? 'The broadcast will not be sent. This removes the record.'
    : 'This removes the record permanently and cannot be undone.';
  const dialogActionLabel = isScheduled ? 'Cancel send' : 'Delete';

  return (
    <>
    <AlertDialog
      open={deleteOpen}
      onOpenChange={setDeleteOpen}
      title={dialogTitle}
      description={dialogBody}
      actionLabel={dialogActionLabel}
      cancelLabel="Keep"
      destructive
      onAction={() => {
        start(async () => {
          await onDelete(broadcastId);
        });
      }}
    />
    <Row gap="xs" align="center" style={{ flexShrink: 0 }}>
      <Button
        href={primaryHref}
        linkAs={RouterLinkAs}
        variant="secondary"
        size="md"
      >
        {primaryLabel}
      </Button>
      <DropdownMenu
        align="end"
        trigger={
          <Button
            variant="secondary"
            size="md"
            icon={I.arrowDown}
            aria-label="More actions"
          />
        }
      >
        {items.map((item) => (
          <DropdownMenuItem
            key={item.key}
            destructive={item.destructive ?? false}
            disabled={pending}
            onSelect={() => {
              start(async () => {
                await item.run(broadcastId);
              });
            }}
          >
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
    </Row>
    </>
  );
}
